/**
 * Error Display Utilities
 *
 * Simple error message extraction and display.
 *
 * Security Approach:
 * - Backend is responsible for sanitizing all error messages
 * - Frontend trusts backend and displays messages as-is
 * - React automatically escapes HTML to prevent XSS attacks
 * - No modification of backend messages (shows exact text)
 */

const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

/**
 * Shown instead of a backend 5xx body. The raw response stays in server logs.
 */
export const SERVER_ERROR_SUPPORT_MESSAGE =
  "Something went wrong on our side. Please try again, or contact support if it continues.";

export function isHttpServerErrorStatus(status: unknown): status is number {
  return typeof status === "number" && status >= 500 && status < 600;
}

function httpStatus(error: object): number | undefined {
  const record = error as Record<string, unknown>;
  if (typeof record.status === "number") return record.status;
  if (typeof record.statusCode === "number") return record.statusCode;

  const result = record.result;
  if (result && typeof result === "object") {
    const nested = (result as { status?: unknown }).status;
    if (typeof nested === "number") return nested;
  }

  return undefined;
}

function actionFailureType(error: object): string | undefined {
  const record = error as {
    name?: string;
    errors?: { type?: string }[];
    result?: { errors?: { type?: string }[] };
  };
  if (record.name === "NetworkError" || record.name === "TimeoutError") {
    return "network";
  }
  return record.errors?.[0]?.type ?? record.result?.errors?.[0]?.type;
}

/** Backend 5xx only. Network and timeout results keep their own message. */
function isCustomerFacingServerError(error: object): boolean {
  if (actionFailureType(error) === "network") return false;
  return isHttpServerErrorStatus(httpStatus(error));
}

/**
 * Production Server Action / RSC throws are replaced with a digest. Showing
 * that string (or "Minified React error #…") hides the backend message.
 */
function isOpaqueProductionError(message: string): boolean {
  return (
    /Minified React error #\d+/i.test(message) ||
    /An error occurred in the Server Components render/i.test(message) ||
    /digest property is included on this error/i.test(message)
  );
}

function extractDetailMessage(detail: unknown): string | undefined {
  if (typeof detail === "string" && detail) {
    return detail;
  }

  if (!Array.isArray(detail) || detail.length === 0) {
    return undefined;
  }

  const firstError = detail[0];
  if (typeof firstError === "string" && firstError) {
    return firstError;
  }

  if (typeof firstError === "object" && firstError !== null) {
    const validationError = firstError as {
      loc?: string[];
      msg?: string;
      message?: string;
    };
    const field = Array.isArray(validationError.loc)
      ? validationError.loc[validationError.loc.length - 1]
      : "field";
    const msg =
      validationError.msg || validationError.message || "Invalid value";
    return `${field}: ${msg}`;
  }

  return undefined;
}

/**
 * Extract and display error message from various error formats
 *
 * This function:
 * 1. Extracts the error message from different error types
 * 2. Returns the exact backend message without modification
 * 3. Relies on React's automatic HTML escaping for XSS prevention
 *
 * @param error - The error to display (string, Error object, or API response)
 * @param fallback - Fallback message if error is empty
 * @returns The error message exactly as backend sent it
 */
export function displayError(
  error: unknown,
  fallback: string = DEFAULT_ERROR_MESSAGE,
): string {
  if (!error) {
    return fallback;
  }

  if (typeof error === "object" && isCustomerFacingServerError(error)) {
    return SERVER_ERROR_SUPPORT_MESSAGE;
  }

  // String error - return as-is unless Next.js replaced it with a digest
  if (typeof error === "string") {
    if (!error || isOpaqueProductionError(error)) {
      return fallback;
    }
    return error;
  }

  // Error object - return message unless Next.js stripped it in production
  if (error instanceof Error) {
    if (isOpaqueProductionError(error.message)) {
      return fallback;
    }
    return error.message || fallback;
  }

  // API error response object — detail is checked first because FastAPI
  // returns structured validation errors in `detail` while `message` is
  // often a generic HTTP status text (e.g. "Request failed").
  if (typeof error === "object" && error !== null) {
    const errorObj = error as Record<string, unknown>;

    const detailMessage = extractDetailMessage(errorObj.detail);
    if (detailMessage) {
      return detailMessage;
    }

    if (typeof errorObj.error === "string" && errorObj.error) {
      return errorObj.error;
    }

    if (
      typeof errorObj.message === "string" &&
      errorObj.message &&
      !isOpaqueProductionError(errorObj.message)
    ) {
      return errorObj.message;
    }

    if (Array.isArray(errorObj.errors) && errorObj.errors.length > 0) {
      const firstError = errorObj.errors[0];
      if (
        typeof firstError === "object" &&
        firstError !== null &&
        typeof (firstError as { message?: string }).message === "string" &&
        (firstError as { message: string }).message
      ) {
        return (firstError as { message: string }).message;
      }
    }
  }

  return fallback;
}

/**
 * Alias for displayError - for consistency with previous API
 */
export function formatUserError(error: unknown, fallback?: string): string {
  return displayError(error, fallback);
}
